import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Typography,
  Spinner,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
} from '@material-tailwind/react';
import {
  DocumentTextIcon,
  DocumentCheckIcon,
  EyeIcon,
  UserGroupIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import ShareDialog from '../../components/ShareDialog';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { getReportsAnalytics } from '../../services/reportApi';
import { UserAuth } from '../../context/AuthContext';
import { friendlyTimeFormatDt } from '../../utils';

function StatCard({ title, value, icon: Icon, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    cyan: 'bg-cyan-500',
    purple: 'bg-purple-500',
  };

  return (
    <Card className="shadow-sm">
      <CardBody className="p-4">
        <div className="flex items-center gap-4">
          <div className={`${colorClasses[color]} p-3 rounded-lg`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div>
            <Typography variant="small" color="blue-gray" className="font-normal opacity-70">
              {title}
            </Typography>
            <Typography variant="h4" color="blue-gray">
              {value}
            </Typography>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function ReportAnalyticsPage() {
  const { user } = UserAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [shareDialogReport, setShareDialogReport] = useState(null);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getReportsAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.uid) {
      loadAnalytics();
    }
  }, [user?.uid]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner className="h-12 w-12" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-12 p-4">
        <Alert color="red" variant="gradient">
          Failed to load analytics: {error}
        </Alert>
      </div>
    );
  }

  const { summary, userActivity, topReports, timeline, recentActivity } = analytics || {};

  return (
    <div className="mt-12 mb-8">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="h4" color="blue-gray">
          Report Analytics
        </Typography>
        <Tooltip content="Refresh">
          <IconButton
            variant="text"
            onClick={loadAnalytics}
            disabled={isLoading}
          >
            <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          </IconButton>
        </Tooltip>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
        <StatCard
          title="Total Reports"
          value={summary?.totalDrafts || 0}
          icon={DocumentTextIcon}
          color="blue"
        />
        <StatCard
          title="Published Versions"
          value={summary?.totalReports || 0}
          icon={DocumentCheckIcon}
          color="green"
        />
        <StatCard
          title="Total Opens"
          value={summary?.totalReportOpened || 0}
          icon={EyeIcon}
          color="amber"
        />
        <StatCard
          title="Total Viewed"
          value={summary?.totalEngagedViews || 0}
          icon={DocumentCheckIcon}
          color="cyan"
        />
        <StatCard
          title="Verified Viewers"
          value={summary?.uniqueViewers || 0}
          icon={UserGroupIcon}
          color="purple"
        />
      </div>

      {/* Timeline Chart */}
      {timeline && timeline.length > 0 && (
        <Card className="mb-8 shadow-sm">
          <CardHeader floated={false} shadow={false} className="rounded-none p-4">
            <Typography variant="h6" color="blue-gray">
              Activity Over Time
            </Typography>
          </CardHeader>
          <CardBody className="p-4 pt-0">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    }}
                  />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Legend />
                  <Bar dataKey="drafts" name="Reports Created" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="reports" name="Versions Published" fill="#22C55E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Two-column layout for tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        {/* Activity by User */}
        <Card className="shadow-sm">
          <CardHeader floated={false} shadow={false} className="rounded-none p-4">
            <Typography variant="h6" color="blue-gray">
              Activity by User
            </Typography>
          </CardHeader>
          <CardBody className="p-4 pt-0 overflow-auto">
            <table className="w-full min-w-max table-auto text-left">
              <thead>
                <tr>
                  {['User', 'Reports', 'Versions', 'Last Active'].map((head) => (
                    <th key={head} className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                      <Typography variant="small" color="blue-gray" className="font-medium opacity-70">
                        {head}
                      </Typography>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!userActivity || userActivity.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-4 text-center">
                      <Typography variant="small" color="blue-gray" className="opacity-70">
                        No user activity found
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  userActivity.map((userData, index) => (
                    <tr key={userData.email} className={index === userActivity.length - 1 ? '' : 'border-b border-blue-gray-50'}>
                      <td className="p-3">
                        <div>
                          <Typography variant="small" color="blue-gray" className="font-medium">
                            {userData.name || userData.email}
                          </Typography>
                          {userData.name && (
                            <Typography variant="small" color="blue-gray" className="font-normal opacity-60">
                              {userData.email}
                            </Typography>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <Typography variant="small" color="blue-gray" className="font-normal">
                          {userData.draftsCount}
                        </Typography>
                      </td>
                      <td className="p-3">
                        <Typography variant="small" color="blue-gray" className="font-normal">
                          {userData.reportsCount}
                        </Typography>
                      </td>
                      <td className="p-3">
                        <Typography variant="small" color="blue-gray" className="font-normal">
                          {userData.lastActivity ? friendlyTimeFormatDt(new Date(userData.lastActivity)) : '-'}
                        </Typography>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>

        {/* Top Reports */}
        <Card className="shadow-sm">
          <CardHeader floated={false} shadow={false} className="rounded-none p-4">
            <Typography variant="h6" color="blue-gray">
              Top Reports
            </Typography>
          </CardHeader>
          <CardBody className="p-4 pt-0 max-h-96 overflow-auto">
            <table className="w-full table-auto text-left text-sm">
              <thead className="sticky top-0 bg-blue-gray-50">
                <tr>
                  <th className="border-b border-blue-gray-100 p-2 whitespace-nowrap">
                    <Typography variant="small" color="blue-gray" className="font-medium opacity-70">
                      Report
                    </Typography>
                  </th>
                  <th className="border-b border-blue-gray-100 p-2 whitespace-nowrap">
                    <Typography variant="small" color="blue-gray" className="font-medium opacity-70">
                      Preparer
                    </Typography>
                  </th>
                  <th className="border-b border-blue-gray-100 p-2 whitespace-nowrap text-center">
                    <Tooltip content="Report page loaded after verification (or directly if no verification required)">
                      <Typography variant="small" color="blue-gray" className="font-medium opacity-70 cursor-help">
                        Opens
                      </Typography>
                    </Tooltip>
                  </th>
                  <th className="border-b border-blue-gray-100 p-2 whitespace-nowrap text-center">
                    <Tooltip content="Viewers who scrolled past the cover page (engaged with content)">
                      <Typography variant="small" color="blue-gray" className="font-medium opacity-70 cursor-help">
                        Viewed
                      </Typography>
                    </Tooltip>
                  </th>
                  <th className="border-b border-blue-gray-100 p-2 whitespace-nowrap text-center">
                    <Tooltip content="Unique viewers who completed email verification">
                      <Typography variant="small" color="blue-gray" className="font-medium opacity-70 cursor-help">
                        Verified
                      </Typography>
                    </Tooltip>
                  </th>
                  <th className="border-b border-blue-gray-100 p-2 whitespace-nowrap">
                    <Typography variant="small" color="blue-gray" className="font-medium opacity-70">
                      Created
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody>
                {!topReports || topReports.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-4 text-center">
                      <Typography variant="small" color="blue-gray" className="opacity-70">
                        No reports yet
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  topReports.map((report, index) => (
                    <tr key={report.reportId} className={`hover:bg-gray-50 ${index === topReports.length - 1 ? '' : 'border-b border-blue-gray-50'}`}>
                      <td className="p-2 max-w-[180px]">
                        <span className="text-gray-800 truncate block">
                          {report.title}
                        </span>
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        <span className="text-gray-600">
                          {report.preparer}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <span className="font-semibold text-green-600">
                          {report.totalReportOpened ?? 0}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <span className="font-medium text-blue-600">
                          {report.totalEngagedViews ?? 0}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <span className="font-medium text-purple-600">
                          {report.uniqueViewers ?? 0}
                        </span>
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        <span className="text-gray-500 text-xs">
                          {report.createdAt ? friendlyTimeFormatDt(new Date(report.createdAt)) : '-'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>

      {/* Recent Activity - Hierarchical Timeline (Draft > Version > Share) */}
      <Card className="shadow-sm">
        <CardHeader floated={false} shadow={false} className="rounded-none p-4">
          <div className="flex items-center justify-between">
            <Typography variant="h6" color="blue-gray">
              Recent Activity
            </Typography>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 transition-colors"
            >
              <InformationCircleIcon className="h-4 w-4" />
              <span>How to read this</span>
              {showHelp ? (
                <ChevronUpIcon className="h-3 w-3" />
              ) : (
                <ChevronDownIcon className="h-3 w-3" />
              )}
            </button>
          </div>

          <Collapse open={showHelp}>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm space-y-2">
              <Typography variant="small" className="font-semibold text-blue-900">
                Understanding the Timeline
              </Typography>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li><strong>Reports</strong> are grouped at the top level (gray header with title and preparer)</li>
                <li><strong>Versions</strong> (v1, v2, etc.) represent each time a report was published</li>
                <li><strong>Shares</strong> are individual share links sent to recipients</li>
                <li>Each share shows a horizontal timeline of events from left (oldest) to right (newest)</li>
              </ul>
              <Typography variant="small" className="font-semibold text-blue-900 mt-3">
                Event Types (Conversion Funnel)
              </Typography>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span><strong>Published</strong> — Report version was published and share link created</li>
                <li><span className="inline-block w-2 h-2 rounded-full bg-gray-400 mr-1"></span><strong>Request</strong> — Link clicked, landed on verification page</li>
                <li><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1"></span><strong>Code Sent</strong> — Verification code sent to recipient's email</li>
                <li><span className="inline-block w-2 h-2 rounded-full bg-purple-500 mr-1"></span><strong>Verified</strong> — Recipient completed email verification</li>
                <li><span className="inline-block w-2 h-2 rounded-full bg-cyan-500 mr-1"></span><strong>Opened</strong> — Report page loaded (after verification)</li>
                <li><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1"></span><strong>Viewed</strong> — Viewer scrolled past cover page (engaged)</li>
              </ul>
              <Typography variant="small" color="blue-gray" className="mt-2 opacity-70">
                Hover over any event dot to see details like timestamp and viewer info.
              </Typography>
            </div>
          </Collapse>

          <div className="flex flex-wrap items-center gap-4 mt-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <Typography variant="small" className="opacity-70">Published</Typography>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-gray-400" />
              <Typography variant="small" className="opacity-70">Request</Typography>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <Typography variant="small" className="opacity-70">Code Sent</Typography>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <Typography variant="small" className="opacity-70">Verified</Typography>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-cyan-500" />
              <Typography variant="small" className="opacity-70">Opened</Typography>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <Typography variant="small" className="opacity-70">Viewed</Typography>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-4 pt-0 overflow-x-auto">
          {!recentActivity || recentActivity.length === 0 ? (
            <Typography variant="small" color="blue-gray" className="opacity-70 text-center py-4">
              No recent activity
            </Typography>
          ) : (
            <div className="space-y-4 min-w-[700px]">
              {(() => {
                // Build 3-level hierarchy: Draft > Version > Share
                const drafts = new Map();

                recentActivity.forEach(activity => {
                  const draftKey = activity.draftId || activity.reportTitle || 'Unknown';
                  const versionKey = activity.reportId || `${draftKey}-v${activity.version || 1}`;
                  const shareKey = activity.shareId || 'default';

                  // Initialize draft
                  if (!drafts.has(draftKey)) {
                    drafts.set(draftKey, {
                      draftId: activity.draftId,
                      title: activity.reportTitle,
                      preparer: activity.preparer,
                      versions: new Map(),
                      lastActivity: null
                    });
                  }
                  const draft = drafts.get(draftKey);

                  // Initialize version
                  if (!draft.versions.has(versionKey)) {
                    draft.versions.set(versionKey, {
                      reportId: activity.reportId,
                      version: activity.version,
                      shares: new Map(),
                      hasPublish: false
                    });
                  }
                  const version = draft.versions.get(versionKey);

                  // Initialize share
                  if (!version.shares.has(shareKey)) {
                    version.shares.set(shareKey, {
                      shareId: activity.shareId,
                      customerEmail: activity.customerEmail,
                      events: []
                    });
                  }
                  version.shares.get(shareKey).events.push(activity);

                  // Track publish events
                  if (activity.type === 'report_created') {
                    version.hasPublish = true;
                  }

                  // Track last activity for sorting
                  if (!draft.lastActivity || new Date(activity.timestamp) > new Date(draft.lastActivity)) {
                    draft.lastActivity = activity.timestamp;
                  }
                });

                // Sort and filter
                const sortedDrafts = Array.from(drafts.values())
                  .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));

                const getEventStyle = (event) => {
                  const type = event.type || event;
                  switch (type) {
                    case 'report_created':
                      return {
                        bg: 'bg-green-500',
                        ring: 'ring-green-200',
                        label: 'Created',
                        description: 'A new report was generated and a share link was created.'
                      };
                    case 'report_opened':
                      // Only show as "Request" if verification was required but not completed
                      if (event.requireVerification && !event.verified) {
                        return {
                          bg: 'bg-gray-400',
                          ring: 'ring-gray-200',
                          label: 'Request',
                          description: 'Link clicked, landed on verification page.'
                        };
                      }
                      // Otherwise it's an "Opened" - either direct open or verified open
                      const methodDesc = event.verified
                        ? (event.verificationMethod === 'cached'
                          ? 'Return visit — recognized from previous verification.'
                          : 'Report page loaded after email verification.')
                        : 'Report page loaded (no verification required).';
                      return {
                        bg: 'bg-cyan-500',
                        ring: 'ring-cyan-200',
                        label: 'Opened',
                        description: methodDesc
                      };
                    case 'code_requested':
                      return {
                        bg: 'bg-amber-500',
                        ring: 'ring-amber-200',
                        label: 'Code Sent',
                        description: 'Viewer requested a verification code to be sent to their email.'
                      };
                    case 'verification_started':
                      return {
                        bg: 'bg-amber-300',
                        ring: 'ring-amber-100',
                        label: 'Verifying',
                        description: 'Viewer began the email verification process.'
                      };
                    case 'verification_completed':
                      return {
                        bg: 'bg-purple-500',
                        ring: 'ring-purple-200',
                        label: 'Verified',
                        description: 'Viewer successfully verified their email address.'
                      };
                    case 'report_viewed':
                      return {
                        bg: 'bg-blue-500',
                        ring: 'ring-blue-200',
                        label: 'Viewed',
                        description: 'Viewer engaged with the report by scrolling past the cover page.'
                      };
                    default:
                      return { bg: 'bg-gray-400', ring: 'ring-gray-200', label: type, description: '' };
                  }
                };

                return sortedDrafts.map((draft, draftIndex) => {
                  const versions = Array.from(draft.versions.values())
                    .filter(v => v.hasPublish)
                    .sort((a, b) => (a.version || 0) - (b.version || 0));

                  if (versions.length === 0) return null;

                  return (
                    <div key={draftIndex} className="bg-gray-50 rounded-lg overflow-hidden">
                      {/* Draft header */}
                      <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
                        <Typography variant="small" color="blue-gray" className="font-semibold">
                          {draft.title}
                        </Typography>
                        {draft.preparer && (
                          <Typography variant="small" color="blue-gray" className="opacity-60">
                            Preparer: {draft.preparer}
                          </Typography>
                        )}
                      </div>

                      {/* Versions */}
                      <div className="divide-y divide-gray-200">
                        {versions.map((version, versionIndex) => {
                          const shares = Array.from(version.shares.values());

                          return (
                            <div key={versionIndex} className="px-4 py-2">
                              {/* Version header */}
                              {(() => {
                                // Count actual shares (those with shareId and share events)
                                const actualShares = shares.filter(s =>
                                  s.shareId && s.events.some(e => e.type !== 'report_created')
                                );
                                const shareCount = actualShares.length;

                                // Calculate totals for this version
                                const allVersionEvents = shares.flatMap(s => s.events);
                                const versionTotals = allVersionEvents.reduce((acc, e) => {
                                  if (e.type === 'report_opened') {
                                    if (e.requireVerification && !e.verified) {
                                      acc.requests++; // Verification page (verification required but not completed)
                                    } else {
                                      acc.opens++; // Report page loaded (direct or verified)
                                    }
                                  }
                                  if (e.type === 'code_requested') acc.codes++;
                                  if (e.type === 'verification_completed') acc.verified++;
                                  if (e.type === 'report_viewed') acc.viewed++;
                                  return acc;
                                }, { requests: 0, codes: 0, verified: 0, opens: 0, viewed: 0 });

                                // Find the publish timestamp
                                const publishEvent = allVersionEvents.find(e => e.type === 'report_created');
                                const publishedAt = publishEvent?.timestamp;

                                return (
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <div className="flex flex-col">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                                        v{version.version || versionIndex + 1}
                                      </span>
                                      {publishedAt && (
                                        <span className="text-[10px] text-gray-400 mt-0.5 text-center">
                                          {friendlyTimeFormatDt(new Date(publishedAt))}
                                        </span>
                                      )}
                                    </div>
                                    <Tooltip content="View share details">
                                      <IconButton
                                        variant="text"
                                        size="sm"
                                        className="p-1 h-6 w-6"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShareDialogReport({
                                            reportId: version.reportId,
                                            title: draft.title,
                                          });
                                        }}
                                      >
                                        <ShareIcon className="h-4 w-4 text-blue-500" />
                                      </IconButton>
                                    </Tooltip>
                                    <Typography variant="small" color="blue-gray" className="opacity-50">
                                      {shareCount} share{shareCount !== 1 ? 's' : ''}
                                    </Typography>
                                    <span className="text-gray-300 mx-1">|</span>
                                    <div className="flex items-center gap-2 text-xs">
                                      {versionTotals.requests > 0 && (
                                        <Tooltip content="Requests (verification page)" className="z-[10000]">
                                          <span className="flex items-center gap-1 cursor-help">
                                            <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                                            <span className="text-gray-600">{versionTotals.requests}</span>
                                          </span>
                                        </Tooltip>
                                      )}
                                      {versionTotals.codes > 0 && (
                                        <Tooltip content="Codes sent" className="z-[10000]">
                                          <span className="flex items-center gap-1 cursor-help">
                                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                            <span className="text-gray-600">{versionTotals.codes}</span>
                                          </span>
                                        </Tooltip>
                                      )}
                                      {versionTotals.verified > 0 && (
                                        <Tooltip content="Verifications completed" className="z-[10000]">
                                          <span className="flex items-center gap-1 cursor-help">
                                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                            <span className="text-gray-600">{versionTotals.verified}</span>
                                          </span>
                                        </Tooltip>
                                      )}
                                      {versionTotals.opens > 0 && (
                                        <Tooltip content="Opened (report loaded)" className="z-[10000]">
                                          <span className="flex items-center gap-1 cursor-help">
                                            <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                                            <span className="text-gray-600">{versionTotals.opens}</span>
                                          </span>
                                        </Tooltip>
                                      )}
                                      {versionTotals.viewed > 0 && (
                                        <Tooltip content="Viewed (scrolled past cover)" className="z-[10000]">
                                          <span className="flex items-center gap-1 cursor-help">
                                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                            <span className="text-gray-600">{versionTotals.viewed}</span>
                                          </span>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Share timelines */}
                              <div className="space-y-2 ml-4">
                                {shares.map((share, shareIndex) => {
                                  // Separate publish events (version-level) from share events
                                  const publishEvents = share.events.filter(e => e.type === 'report_created');
                                  const shareEvents = share.events.filter(e => e.type !== 'report_created');

                                  // Skip shares that only have no shareId and no share events (orphaned publishes)
                                  const isOrphanedPublish = !share.shareId && shareEvents.length === 0;

                                  // If this is an orphaned publish, we'll render it differently
                                  if (isOrphanedPublish && publishEvents.length > 0) {
                                    // Render publish at version level (first share row)
                                    if (shareIndex === 0) {
                                      return (
                                        <div key={shareIndex} className="flex items-center gap-3">
                                          <div className="w-40 flex-shrink-0">
                                            <Typography variant="small" color="blue-gray" className="truncate opacity-70 italic">
                                              Version Published
                                            </Typography>
                                          </div>
                                          <div className="flex-1 relative py-2">
                                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-300 -translate-y-1/2" />
                                            <div className="relative flex items-center gap-2">
                                              {publishEvents.map((event, eventIndex) => {
                                                const style = getEventStyle(event.type);
                                                return (
                                                  <Tooltip
                                                    key={eventIndex}
                                                    content={
                                                      <div className="p-1 text-xs">
                                                        <div className="font-semibold">{style.label}</div>
                                                        <div className="opacity-80">
                                                          {event.timestamp ? new Date(event.timestamp).toLocaleString() : '-'}
                                                        </div>
                                                      </div>
                                                    }
                                                  >
                                                    <div
                                                      className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer transition-transform hover:scale-125 ${style.bg} ring-2 ${style.ring}`}
                                                    >
                                                      <DocumentCheckIcon className="h-3 w-3 text-white" />
                                                    </div>
                                                  </Tooltip>
                                                );
                                              })}
                                            </div>
                                          </div>
                                          <div className="w-24 flex-shrink-0 text-right">
                                            <Typography variant="small" color="blue-gray" className="opacity-50 text-xs">
                                              {publishEvents[0]?.timestamp
                                                ? friendlyTimeFormatDt(new Date(publishEvents[0].timestamp))
                                                : '-'}
                                            </Typography>
                                          </div>
                                        </div>
                                      );
                                    }
                                    return null; // Skip subsequent orphaned publish shares
                                  }

                                  // For real shares, skip if no share events
                                  if (shareEvents.length === 0) return null;

                                  const sortedEvents = [...shareEvents].sort(
                                    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
                                  );

                                  return (
                                    <div key={shareIndex} className="flex items-center gap-3">
                                      {/* Share label */}
                                      <div className="w-40 flex-shrink-0">
                                        <Typography variant="small" color="blue-gray" className="truncate opacity-70">
                                          {share.customerEmail || (share.shareId ? `Share ${share.shareId.slice(-6)}` : `Share ${shareIndex + 1}`)}
                                        </Typography>
                                      </div>

                                      {/* Timeline */}
                                      <div className="flex-1 relative py-2 overflow-x-auto">
                                        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-300 -translate-y-1/2" />
                                        <div className="relative flex items-center gap-2 w-max min-w-full">
                                          {sortedEvents.map((event, eventIndex) => {
                                            const style = getEventStyle(event);
                                            return (
                                              <Tooltip
                                                key={eventIndex}
                                                content={
                                                  <div className="p-1 text-xs max-w-[220px]">
                                                    <div className="font-semibold">{style.label}</div>
                                                    {style.description && (
                                                      <div className="opacity-70 text-[10px] mb-1">{style.description}</div>
                                                    )}
                                                    <div className="opacity-80 border-t border-gray-600 pt-1 mt-1">
                                                      {event.timestamp ? new Date(event.timestamp).toLocaleString() : '-'}
                                                    </div>
                                                    {event.userEmail && event.userEmail !== 'anonymous' && (
                                                      <div className="opacity-80">{event.userEmail}</div>
                                                    )}
                                                    {event.verificationMethod && (
                                                      <div className="opacity-70 text-[10px]">
                                                        {event.verificationMethod === 'cached' ? '(Return visit)' : '(First-time verification)'}
                                                      </div>
                                                    )}
                                                    {event.location && (event.location.city || event.location.country) && (
                                                      <div className="opacity-70 text-[10px] mt-1">
                                                        📍 {[event.location.city, event.location.region, event.location.country].filter(Boolean).join(', ')}
                                                      </div>
                                                    )}
                                                  </div>
                                                }
                                              >
                                                <div
                                                  className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer transition-transform hover:scale-125 ${style.bg} ring-2 ${style.ring}`}
                                                >
                                                  {event.type === 'report_created' && (
                                                    <DocumentCheckIcon className="h-3 w-3 text-white" />
                                                  )}
                                                  {event.type === 'report_viewed' && (
                                                    <EyeIcon className="h-3 w-3 text-white" />
                                                  )}
                                                  {event.type === 'report_opened' && (
                                                    <span className="text-white text-[10px] font-bold">↗</span>
                                                  )}
                                                  {event.type === 'verification_started' && (
                                                    <span className="text-white text-[10px] font-bold">...</span>
                                                  )}
                                                  {event.type === 'code_requested' && (
                                                    <span className="text-white text-[10px] font-bold">#</span>
                                                  )}
                                                  {event.type === 'verification_completed' && (
                                                    <span className="text-white text-[10px] font-bold">✓</span>
                                                  )}
                                                </div>
                                              </Tooltip>
                                            );
                                          })}
                                        </div>
                                      </div>

                                      {/* Last event time */}
                                      <div className="w-24 flex-shrink-0 text-right">
                                        <Typography variant="small" color="blue-gray" className="opacity-50 text-xs">
                                          {sortedEvents.length > 0 && sortedEvents[sortedEvents.length - 1].timestamp
                                            ? friendlyTimeFormatDt(new Date(sortedEvents[sortedEvents.length - 1].timestamp))
                                            : '-'}
                                        </Typography>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Share Dialog */}
      <ShareDialog
        open={!!shareDialogReport}
        onClose={() => setShareDialogReport(null)}
        reportId={shareDialogReport?.reportId}
        reportTitle={shareDialogReport?.title}
        reportType={shareDialogReport?.reportType}
      />
    </div>
  );
}

export default ReportAnalyticsPage;
